import React, { createContext, useContext, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, Button, ButtonBase, InputBase, MenuItem, Select, Slider, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import PickPopover, { SEARCH_FROM } from './PickPopover';
import { PaneSectionId, paneSectionOf, settingMatches, usePanePresentation, usePaneSectionId } from './PaneSettingsLayout';

const FindContext = createContext('');
const SettingScopeContext = createContext<{ id: string; label: string } | null>(null);

export const useSettingsQuery = () => useContext(FindContext);
export const SettingScope = SettingScopeContext.Provider;

export const FIELD = {
  fontSize: 13,
  borderRadius: 1,
  px: 1,
  minHeight: 28,
  transition: 'background-color 120ms, box-shadow 120ms',
  '&:hover': { bgcolor: 'action.hover' },
  border: '1px solid transparent',
  '&.Mui-focused': { bgcolor: 'background.paper', borderColor: 'primary.main' },
  '& input, & textarea': { py: 0.5 },
} as const;

export const NAMED_EMPTY = { InputLabelProps: { shrink: true }, SelectProps: { displayEmpty: true } } as const;

export const SEGMENTED = {
  bgcolor: 'action.hover',
  borderRadius: 1.5,
  p: '2px',
  gap: '2px',
  width: '100%',
  '& .MuiToggleButtonGroup-grouped': { border: 0, borderRadius: '6px !important', m: 0 },
  '& .MuiToggleButton-root': {
    flex: 1,
    minHeight: 26,
    px: 1,
    py: 0.25,
    fontSize: 12.5,
    lineHeight: 1.2,
    textTransform: 'none',
    color: 'text.secondary',
    whiteSpace: 'nowrap',
    '&.Mui-selected, &.Mui-selected:hover': { bgcolor: 'background.paper', color: 'text.primary', boxShadow: '0 1px 2px rgba(15, 12, 8, 0.12)' },
  },
} as const;

const PICKER = {
  ...FIELD,
  bgcolor: 'action.hover',
  width: '100%',
  '& .MuiSelect-select': { py: 0.5, pl: 0, pr: '24px !important', minHeight: 'unset' },
  '& .MuiSvgIcon-root': { fontSize: 18, color: 'text.secondary', right: 4 },
} as const;

export function SettingsFind({ children }: { children: React.ReactNode }) {
  const [query, setQuery] = useState('');
  const presentation = usePanePresentation();
  if (presentation) return <FindContext.Provider value={presentation.query}>{children}</FindContext.Provider>;
  return (
    <>
      <InputBase
        inputProps={{ 'aria-label': 'Find a setting' }}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || !query) return;
          event.stopPropagation();
          setQuery('');
        }}
        placeholder="Find a setting"
        startAdornment={<SearchIcon sx={{ color: 'text.secondary', fontSize: 16, mr: 0.75 }} />}
        sx={{ ...FIELD, bgcolor: 'action.hover', mb: 1, width: '100%' }}
        value={query}
      />
      <FindContext.Provider value={query.trim().toLowerCase()}>{children}</FindContext.Provider>
    </>
  );
}

function storedOpen(id: string, fallback: boolean) {
  try {
    const stored = window.localStorage.getItem(`lens-settings:${id}`);
    return stored === null ? fallback : stored === '1';
  } catch {
    return fallback;
  }
}

export function Section({
  id,
  title,
  summary,
  defaultOpen = false,
  expanded,
  children,
}: {
  id: string;
  title: string;
  summary?: React.ReactNode;
  defaultOpen?: boolean;
  expanded?: boolean;
  children: React.ReactNode;
}) {
  const query = useSettingsQuery();
  const presentation = usePanePresentation();
  const [open, setOpen] = useState(() => expanded || storedOpen(id, defaultOpen));
  useEffect(() => {
    if (expanded) setOpen(true);
  }, [expanded]);
  useEffect(() => {
    if (presentation && (id === 'every' || id === 'lens-every')) setOpen(false);
  }, [Boolean(presentation), id]);
  useEffect(() => {
    if (presentation?.focusKey === id || presentation?.focusKey?.startsWith(`${id}:`)) setOpen(true);
  }, [presentation?.focusKey, id]);
  const own = Boolean(query) && title.toLowerCase().includes(query);
  const shown = open || Boolean(query);
  const toggle = () => {
    setOpen(!shown);
    try {
      window.localStorage.setItem(`lens-settings:${id}`, shown ? '0' : '1');
    } catch {
      return;
    }
  };
  if (presentation && paneSectionOf(id) !== presentation.section && !query) return null;
  return (
    <PaneSectionId value={id}>
      <Box
        data-setting-key={id}
        data-setting-section={paneSectionOf(id)}
        data-settings-section={id}
        data-settings-title={title}
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          ...(query && !own ? { '&:not(:has([data-setting-row]))': { display: 'none' } } : {}),
        }}
      >
        <ButtonBase
          aria-expanded={shown}
          onClick={toggle}
          sx={{
            alignItems: 'center',
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-start',
            py: 1.25,
            textAlign: 'left',
            width: '100%',
          }}
        >
          <Typography component="span" sx={{ flexShrink: 0, fontSize: 13, fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography
            color="text.secondary"
            component="span"
            noWrap
            sx={{ flex: 1, fontSize: 12.5, minWidth: 0, textAlign: 'right' }}
          >
            {shown ? '' : summary}
          </Typography>
          <ExpandMoreIcon
            sx={{
              color: 'text.secondary',
              fontSize: 18,
              transform: shown ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms',
            }}
          />
        </ButtonBase>
        {shown ? (
          <FindContext.Provider value={own ? '' : query}>
            <Stack
              spacing={0.5}
              sx={{
                pb: 1.75,
                ...(query && !own
                  ? { '& > :not([data-setting-row]):not(:has([data-setting-row]))': { display: 'none' } }
                  : {}),
              }}
            >
              {children}
            </Stack>
          </FindContext.Provider>
        ) : null}
      </Box>
    </PaneSectionId>
  );
}

export function Row({
  label,
  keywords = [],
  set = false,
  stacked = false,
  defaultValue,
  onReset,
  resetDisabled = false,
  children,
}: {
  label: string;
  keywords?: Array<string>;
  set?: boolean;
  stacked?: boolean;
  defaultValue?: string;
  onReset?: () => void;
  resetDisabled?: boolean;
  children: React.ReactNode;
}) {
  const query = useSettingsQuery();
  const presentation = usePanePresentation();
  const sectionId = usePaneSectionId();
  const scope = useContext(SettingScopeContext);
  const resultLabel = scope ? `${scope.label} · ${label}` : label;
  const searchTerms = scope ? [...keywords, `${scope.label} ${label}`] : keywords;
  const key = `${sectionId}:${scope ? `${scope.id}:` : ''}${label}`;
  if (!settingMatches(query, resultLabel, searchTerms)) return null;
  return (
    <Box
      data-setting-row={label}
      data-setting-result={resultLabel}
      data-setting-key={key}
      data-setting-keywords={searchTerms.join('|')}
      data-setting-section={paneSectionOf(sectionId)}
      data-setting-default={defaultValue}
      sx={{
        alignItems: stacked ? 'stretch' : 'center',
        columnGap: 1.5,
        display: 'grid',
        gridTemplateColumns: stacked ? 'minmax(0, 1fr)' : '108px minmax(0, 1fr)',
        minHeight: 32,
        rowGap: 0.5,
      }}
    >
      <Typography
        color="text.secondary"
        component="div"
        sx={{
          alignItems: 'center',
          display: 'flex',
          fontSize: 12.5,
          gap: 0.75,
          lineHeight: 1.3,
          pt: stacked ? 0.5 : 0,
        }}
      >
        {label}
        {set ? (
          <Box
            aria-hidden
            component="span"
            sx={{ bgcolor: 'primary.main', borderRadius: '50%', flexShrink: 0, height: 5, width: 5 }}
            title="Set here"
          />
        ) : null}
        {presentation && set && onReset ? (
          <Button aria-label={`Reset ${resultLabel}`} disabled={resetDisabled} onClick={onReset} size="small" sx={{ fontSize: 10, minWidth: 0, p: 0, textTransform: 'none' }}>
            Reset
          </Button>
        ) : null}
      </Typography>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

const fitsSegmented = (choices: Array<[string, string, string?]>) => choices.length <= 4 && choices.reduce((total, [, text]) => total + text.length + 3, 0) <= 30;

export function Picker({ label, value, choices, disabled, onChange, placeholder }: { label: string; value: string; choices: Array<[string, React.ReactNode, boolean?]>; disabled?: boolean; onChange: (value: string) => void; placeholder?: string }) {
  if (choices.length >= SEARCH_FROM) return (
    <PickPopover clearable={false} disabled={disabled} label={label} onPick={(next) => next[0] !== value && onChange(next[0])} options={choices.map(([value, label, disabled]) => ({ value, label, disabled }))} picked={[value]} single>
      {choices.find(([option]) => option === value)?.[1] ?? placeholder ?? value}
    </PickPopover>
  );
  return (
    <Select
      SelectDisplayProps={{ 'aria-label': label } as React.HTMLAttributes<HTMLDivElement>}
      disableUnderline
      disabled={disabled}
      displayEmpty
      onChange={(event) => event.target.value !== value && onChange(String(event.target.value))}
      renderValue={placeholder !== undefined && !value && !choices.some(([option]) => option === '') ? () => <Box sx={{ color: 'text.secondary' }}>{placeholder}</Box> : undefined}
      sx={PICKER}
      value={value}
      variant="standard"
    >
      {choices.map(([option, text, off]) => (
        <MenuItem dense disabled={off} key={option} value={option}>
          {text}
        </MenuItem>
      ))}
    </Select>
  );
}

export function Choice({
  label,
  name = label,
  value,
  choices,
  keywords = [],
  disabled,
  onChange,
  inherit,
}: {
  label: string;
  name?: string;
  value: string;
  choices: Array<[string, string, string?]>;
  keywords?: Array<string>;
  disabled?: boolean;
  onChange: (value: string) => void;
  inherit?: string;
}) {
  const defaultValue = inherit;
  const set = defaultValue !== undefined && value !== defaultValue;
  return (
    <Row
      defaultValue={choices.find(([option]) => option === inherit)?.[1]}
      keywords={[...keywords, ...choices.map(([, text]) => text)]}
      label={label}
      onReset={set && defaultValue !== undefined ? () => onChange(defaultValue) : undefined}
      resetDisabled={disabled}
      set={set}
    >
      {fitsSegmented(choices) ? (
        <ToggleButtonGroup
          aria-label={name}
          disabled={disabled}
          exclusive
          onChange={(_, next: string | null) => next !== null && next !== value && onChange(next)}
          size="small"
          sx={SEGMENTED}
          value={value}
        >
          {choices.map(([option, text, name]) => (
            <ToggleButton aria-label={name} key={option} value={option}>
              {text}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : (
        <Picker
          choices={choices.map(([option, text]) => [option, text])}
          disabled={disabled}
          label={name}
          onChange={onChange}
          value={value}
        />
      )}
    </Row>
  );
}

const PIXEL_SIZES = [11, 12, 13, 14, 16, 18, 20, 24];

const TEXT_SIZES: Array<[string, string]> = [
  ['small', 'Small'],
  ['medium', 'Regular'],
  ['large', 'Large'],
];

type SizeOption = { value: string | number | null; label: string };

export function SizeSetting({ label, value, named, pixels, range, disabled, onChange }: { label: string; value: string | number | null; named: Array<SizeOption>; pixels: Array<number>; range: [number, number]; disabled?: boolean; onChange: (next: string | number | null) => void }) {
  const options: Array<SizeOption> = [...named, ...pixels.map((px) => ({ value: px, label: `${px} px` }))];
  const current = options.find((option) => option.value === value) ?? { value, label: `${String(value)} px` };
  const defaultOption = named.find((option) => option.value === null) ?? named.find((option) => option.value === 'medium');
  const typed = (text: string) => {
    const pixels = Math.round(Number(text.replace(/px$/i, '').trim()));
    const match = options.find((option) => option.label.toLowerCase() === text.trim().toLowerCase());
    if (match) onChange(match.value);
    else if (pixels >= range[0] && pixels <= range[1]) onChange(pixels);
  };
  return (
    <Row defaultValue={defaultOption?.label} keywords={['font', 'pixels', 'px']} label={label} onReset={defaultOption && value !== defaultOption.value ? () => onChange(defaultOption.value) : undefined} resetDisabled={disabled} set={defaultOption ? value !== defaultOption.value : false}>
      <Autocomplete
        autoSelect
        disableClearable
        disabled={disabled}
        forcePopupIcon
        freeSolo
        fullWidth
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        isOptionEqualToValue={(option, chosen) => option.value === chosen.value}
        onChange={(_, next) => (typeof next === 'string' ? typed(next) : next.value !== current.value && onChange(next.value))}
        options={options}
        renderInput={(params) => <TextField {...params} inputProps={{ ...params.inputProps, 'aria-label': label }} placeholder="Size or px" size="small" sx={{ '& .MuiOutlinedInput-root': { ...FIELD, bgcolor: 'action.hover', py: '1px !important' }, '& fieldset': { border: 0 } }} />}
        size="small"
        value={current}
      />
    </Row>
  );
}

export function TextSizeSetting({ value, inherit, disabled, onChange }: { value: unknown; inherit?: string; disabled?: boolean; onChange: (next: string | number | null) => void }) {
  return (
    <SizeSetting
      disabled={disabled}
      label="Text size"
      named={[...(inherit ? [{ value: null, label: inherit }] : []), ...TEXT_SIZES.map(([size, label]) => ({ value: size, label }))]}
      onChange={onChange}
      pixels={PIXEL_SIZES}
      range={[9, 24]}
      value={(value as string | number | undefined) ?? (inherit ? null : 'medium')}
    />
  );
}

export function Reveal({ label, open = false, children }: { label: string; open?: boolean; children: (close: () => void) => React.ReactNode }) {
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) setShown(true);
  }, [open]);
  if (shown) return <>{children(() => setShown(false))}</>;
  return (
    <Box>
      <Button onClick={() => setShown(true)} size="small" startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />} sx={{ fontSize: 12.5, minWidth: 0, px: 0.75, textTransform: 'none' }} variant="text">
        {label}
      </Button>
    </Box>
  );
}

export function SnapSlider({ label, value, min, max, step, marks, snap, unit, show, read, set, disabled, onCommit, onReset }: { label: string; value: number; min: number; max: number; step: number; marks: Array<number>; snap: number; unit: string; show: (value: number) => string; read: (text: string) => number; set?: boolean; disabled?: boolean; onCommit: (value: number) => void; onReset?: () => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const settle = (event: Event | React.SyntheticEvent, next: number) => (event.type.startsWith('key') ? next : marks.find((mark) => Math.abs(mark - next) <= snap) ?? next);
  const commit = (next: number) => next !== value && onCommit(next);
  return (
    <Row label={label} onReset={set ? onReset : undefined} resetDisabled={disabled} set={set}>
      <Slider
        aria-label={label}
        disabled={disabled}
        marks={marks.map((mark) => ({ value: mark }))}
        max={max}
        min={min}
        onChange={(event, next) => setDraft(settle(event, next as number))}
        onChangeCommitted={(event, next) => commit(settle(event, next as number))}
        shiftStep={step * 10}
        size="small"
        step={step}
        sx={{ flex: 1, mx: 1 }}
        value={draft}
      />
      <Box sx={{ flexShrink: 0, width: 72 }}>
        <InputBase
          disabled={disabled}
          endAdornment={show(draft) ? <Typography color="text.secondary" sx={{ fontSize: 12, pl: 0.25 }}>{unit}</Typography> : null}
          inputProps={{ 'aria-label': `${label} in ${unit}`, inputMode: 'decimal', style: { textAlign: 'right' } }}
          key={draft}
          defaultValue={show(draft)}
          onBlur={(event) => commit(Math.min(max, Math.max(min, read(event.target.value))))}
          onKeyDown={(event) => event.key === 'Enter' && (event.target as HTMLInputElement).blur()}
          placeholder="Auto"
          sx={{ ...FIELD, bgcolor: 'action.hover', px: 0.75 }}
        />
      </Box>
    </Row>
  );
}

export function TextInput({ label, value, shown, placeholder, multiline, onCommit, size = 'regular', disabled }: { label: string; value: string; shown?: string; placeholder?: string; multiline?: boolean; onCommit: (next: string) => void; size?: 'regular' | 'title'; disabled?: boolean }) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    if (draft.trim() !== value) onCommit(draft.trim());
  };
  return (
    <InputBase
      disabled={disabled}
      fullWidth
      inputProps={{ 'aria-label': label }}
      maxRows={12}
      minRows={multiline ? 1 : undefined}
      multiline={multiline}
      onBlur={() => {
        setFocused(false);
        commit();
      }}
      onChange={(event) => setDraft(event.target.value)}
      onFocus={() => setFocused(true)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          commit();
        }
      }}
      placeholder={placeholder}
      sx={{ ...FIELD, ...(multiline ? { bgcolor: 'action.hover', py: 0.5 } : {}), ...(size === 'title' ? { fontSize: 16, fontWeight: 600, mx: -1 } : {}) }}
      value={focused || !shown ? draft : shown}
    />
  );
}

export function TextRow({ label, value, placeholder, multiline, onCommit, set }: { label: string; value: string; placeholder?: string; multiline?: boolean; onCommit: (next: string) => void; set?: boolean }) {
  return (
    <Row defaultValue="Empty" label={label} onReset={set ? () => onCommit('') : undefined} set={set} stacked={multiline}>
      <TextInput label={label} multiline={multiline} onCommit={onCommit} placeholder={placeholder ?? (multiline ? undefined : 'None')} value={value} />
    </Row>
  );
}

export function SwitchRow({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
  return (
    <Row defaultValue="Off" label={label} onReset={checked ? () => onChange(false) : undefined} resetDisabled={disabled} set={checked}>
      <Switch checked={checked} disabled={disabled} slotProps={{ input: { 'aria-label': label } }} onChange={(event) => onChange(event.target.checked)} size="small" sx={{ ml: -1 }} />
    </Row>
  );
}

export function SubHeading({ children }: { children: React.ReactNode }) {
  const query = useSettingsQuery();
  if (query) return null;
  return (
    <Typography sx={{ color: 'text.primary', fontSize: 12.5, fontWeight: 500, pt: 1 }}>
      {children}
    </Typography>
  );
}

export function Actions({ children }: { children: React.ReactNode }) {
  return <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ '& .MuiButton-root': { fontSize: 12.5, minWidth: 0, px: 1, textTransform: 'none' }, mx: -1 }}>{children}</Stack>;
}

export function PanelHeader({ back, title, onBack, children }: { back?: string; title: React.ReactNode; onBack?: () => void; children?: React.ReactNode }) {
  return (
    <Stack spacing={0.25} sx={{ mb: 1 }}>
      {back ? (
        <ButtonBase onClick={onBack} sx={{ alignSelf: 'flex-start', borderRadius: 1, color: 'text.secondary', fontSize: 12.5, gap: 0.5, mx: -0.5, px: 0.5, py: 0.25, '&:hover': { color: 'text.primary' } }}>
          <ArrowBackIcon sx={{ fontSize: 14 }} />
          {back}
        </ButtonBase>
      ) : null}
      <Stack alignItems="center" direction="row" spacing={0.5}>
        <Typography component="h3" sx={{ flex: 1, fontSize: 16, fontWeight: 600, minWidth: 0 }}>
          {title}
        </Typography>
        {children}
      </Stack>
    </Stack>
  );
}
